import React, { useEffect, useState, useRef } from 'react';
import { MotionConfig } from 'framer-motion';
import { useAppStore } from './app/hooks/useAppStore';
import { useAssessmentSession } from './assessment/hooks/useAssessmentSession';
import { useAppNavigation } from './app/hooks/useAppNavigation';
import { usePracticeHandlers } from './practice/hooks/usePracticeSession';
import { getAllQuestions, ensureAllContentLoaded, prefetchAllContentWhenIdle, isContentLoaded, type ModuleId, type Question } from './content';
import { isSessionExpired, findAccountByLinkCode, generateClassroomId, type Classroom } from './accounts';
import { AuthGuard } from './app/AuthGuard';
import { AppContent } from './app/AppContent';
import { assertSupportCoverage } from './support';
import { warn, error as logError } from './utils/logger';
import { SESSION_CHECK_INTERVAL_MS } from './config/constants';
import { dataLayer } from './data/dataLayer';
import { loadV11ConfigOverrides } from './mastery/selection/v11ConfigStore';
// Apply V11_CONFIG runtime overrides as early as possible (P-14).
// Fire-and-forget: failures fall back to defaults.
loadV11ConfigOverrides().catch(() => {
  /* non-fatal */
});
export function App() {
  const store = useAppStore();
  const {
    state,
    accounts,
    activeAccount,
    selectedGroup,
    setSelectedGroup,
    startingPeriod,
    manualPeriod,
    setStartingPeriod,
    recordAnswer,
    markCheatSheetViewed,
    clearPendingLevelUp,
    updateProfileDisplayName,
    switchAccount,
    isInitialized,
    loadDemoAccounts,
    currentLearnerDisplayName,
    requiresViewingLearnerSelection,
    getLearnerData,
    getLearnerModuleMastery,
    viewingLearnerId,
    setViewingLearner
  } = store;
  // ── Content System Initialization (non-blocking) ──────────────────
  // Boot Flow Refactor (mei 2026): content is loaded in the background
  // so pre-auth screens (Welcome / Login / Register) render instantly.
  // Assessment / practice gate themselves via `isReady`.
  //
  // We must use useState (not useRef) for the questions array so that
  // re-renders after async load / HMR / idle prefetch pick up the data.
  const [contentReady, setContentReady] = useState<boolean>(() => isContentLoaded());
  const [contentError, setContentError] = useState<string | null>(null);
  const [questions, setQuestions] = useState<Question[]>(() => {
    if (!isContentLoaded()) return [];
    try {
      return getAllQuestions();
    } catch {
      return [];
    }
  });
  const nav = useAppNavigation({
    accounts,
    activeAccount,
    isInitialized,
    switchAccount,
    setViewingLearner,
    loadDemoAccounts,
    clearActiveAccount: store.clearActiveAccount
  });
  // Trigger content load once the user passes the welcome screen, OR via
  // idle-prefetch after first paint as a fallback for offline parity.
  useEffect(() => {
    if (contentReady) return;
    let cancelled = false;
    const finishLoad = () => {
      try {
        const q = getAllQuestions();
        if (q.length === 0) warn('No questions loaded - check question data files');
        if (process.env.NODE_ENV === 'development') assertSupportCoverage(q);
        setQuestions(q);
      } catch (err) {
        logError('Failed to process questions:', err);
        setQuestions([]);
      }
      setContentReady(true);
    };
    const load = () => {
      ensureAllContentLoaded().then(() => {
        if (cancelled) return;
        finishLoad();
      }).catch(err => {
        if (cancelled) return;
        logError('Content system initialization failed:', err);
        setContentError('Kon leerinhoud niet laden. Probeer de pagina te vernieuwen.');
      });
    };
    if (!nav.showWelcome) {
      // User has chosen a path (demo / login / register-success) — load now.
      load();
      return () => {
        cancelled = true;
      };
    }
    // Pre-auth: schedule an idle prefetch so the cache is warm by the
    // time they pick a path. Non-blocking. Poll so we sync local state
    // once prefetch (or any other consumer) finishes.
    prefetchAllContentWhenIdle();
    const pollId = window.setInterval(() => {
      if (cancelled) return;
      if (isContentLoaded()) {
        finishLoad();
        window.clearInterval(pollId);
      }
    }, 500);
    return () => {
      cancelled = true;
      window.clearInterval(pollId);
    };
  }, [nav.showWelcome, contentReady]);
  const QUESTIONS = questions;
  // Load reducedMotion preference from dataLayer (D5)
  const [reducedMotionPref, setReducedMotionPref] = useState<boolean>(false);
  useEffect(() => {
    let cancelled = false;
    dataLayer.getLearnerPreferences().then(prefs => {
      if (!cancelled) setReducedMotionPref(prefs.reducedMotion);
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);
  const practice = usePracticeHandlers({
    state,
    selectedGroup,
    markCheatSheetViewed,
    enterPracticeView: nav.enterPracticeView,
    resetToDashboard: nav.resetToDashboard,
    resetToLearningLab: nav.resetToLearningLab
  });
  const assessment = useAssessmentSession({
    familyMastery: state.familyMastery || {},
    selectedGroup,
    recordAnswer,
    allQuestions: QUESTIONS,
    isReady: contentReady && isInitialized && !nav.showWelcome,
    learnerId: viewingLearnerId || activeAccount?.id
  });
  // ── Session Timeout Enforcement (Spec 8.3) ─────────────────────────
  const activeAccountRef = useRef(activeAccount);
  activeAccountRef.current = activeAccount;
  useEffect(() => {
    if (!isInitialized || nav.showWelcome) return;
    if (nav.currentNavView === 'login' || nav.currentNavView === 'register') return;
    const checkSessionTimeout = () => {
      const acct = activeAccountRef.current;
      if (acct && acct.lastActive) {
        const expired = isSessionExpired(acct.lastActive, acct.role);
        if (expired) nav.handleLogout();
      }
    };
    checkSessionTimeout();
    const interval = setInterval(checkSessionTimeout, SESSION_CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isInitialized, activeAccount?.id, activeAccount?.role, nav.showWelcome, nav.currentNavView]);
  // ── Assessment handlers ──────────────────────────────────────────
  const handleStartAssessment = (config: import('./assessment/types').AssessmentConfig) => {
    const result = assessment.startAssessment(config);
    if (result) nav.setCurrentView('assessment-session');
  };
  const handleAssessmentComplete = () => {
    // Capture assignmentId before completeAssessment clears the state
    const assignmentId = assessment.assessmentAttempt?.assignmentId;
    assessment.completeAssessment();
    // Mark assignment as completed by this student so it won't reappear
    if (assignmentId && store.viewingLearnerId) {
      dataLayer.markAssignmentCompletedByStudent(assignmentId, store.viewingLearnerId).catch(e => logError('Failed to mark assignment completed', e));
    } else if (assignmentId && activeAccount?.role === 'learner' && activeAccount?.id) {
      dataLayer.markAssignmentCompletedByStudent(assignmentId, activeAccount.id).catch(e => logError('Failed to mark assignment completed', e));
    }
    nav.resetToDashboard();
    nav.setCurrentNavView('assessmentReport');
  };
  const handleResumeAssessment = () => {
    const result = assessment.resumeAssessment();
    if (result) nav.setCurrentView('assessment-session');
  };
  // ── Render ─────────────────────────────────────────────────────────
  // Content load failure: dedicated error screen.
  if (contentError) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center" role="alert" data-id="element-749">
        <div className="text-center space-y-4 max-w-sm px-4" data-id="element-750">
          <div className="text-3xl" data-id="element-751">⚠️</div>
          <p className="text-slate-700 font-medium" data-id="element-752">{contentError}</p>
          <button onClick={() => window.location.reload()} className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors" data-id="element-753">
            Pagina vernieuwen
          </button>
        </div>
      </div>;
  }
  return <MotionConfig reducedMotion={reducedMotionPref ? 'always' : 'user'} data-id="element-754">
      <AuthGuard showWelcome={nav.showWelcome} currentNavView={nav.currentNavView} accounts={accounts} activeAccount={activeAccount} onLoadDemo={nav.handleLoadDemoAccounts} onNavigateToLogin={nav.handleNavigateToLogin} onNavigateToRegister={nav.handleNavigateToRegister} onLoginSuccess={nav.handleLoginSuccess} onBack={() => nav.setShowWelcome(true)} createAccount={store.createAccount} onJoinClassroom={joinCode => {
      const result = store.joinClassroomByCode(activeAccount?.id || '', joinCode);
      if (result) {
        return {
          success: true
        };
      }
      return {
        success: false,
        error: 'Koppelcode niet gevonden. Controleer de code en probeer opnieuw.'
      };
    }} onLinkChild={linkCode => {
      const found = findAccountByLinkCode(accounts, linkCode);
      if (found && activeAccount) {
        store.linkParentToLearnerById(activeAccount.id, found.id);
        return {
          success: true
        };
      }
      return {
        success: false,
        error: 'Koppelcode niet gevonden. Vraag de code aan de docent van je kind.'
      };
    }} onCreateClassroom={(name, grade) => {
      if (activeAccount) {
        const classroom: Classroom = {
          id: generateClassroomId(),
          name,
          groupLevel: grade,
          learnerIds: [],
          createdAt: Date.now(),
          teacherId: activeAccount.id,
          joinCode: ''
        };
        store.addClassroom(activeAccount.id, classroom);
      }
    }} onJoinExistingClassroom={(classroomId, mode) => {
      if (activeAccount) {
        if (mode === 'transfer') {
          store.transferClassroomOwnership(classroomId, activeAccount.id);
        } else {
          store.joinClassroomAsCoTeacher(activeAccount.id, classroomId);
        }
      }
    }} onCompleteOnboarding={() => store.completeOnboarding()} data-id="element-755">
        <AppContent questions={QUESTIONS} state={state} accounts={accounts} activeAccount={activeAccount} selectedGroup={selectedGroup} setSelectedGroup={setSelectedGroup} startingPeriod={startingPeriod} manualPeriod={manualPeriod} setStartingPeriod={setStartingPeriod} recordAnswer={recordAnswer} markCheatSheetViewed={markCheatSheetViewed} clearPendingLevelUp={clearPendingLevelUp} updateProfileDisplayName={updateProfileDisplayName} currentLearnerDisplayName={currentLearnerDisplayName} requiresViewingLearnerSelection={requiresViewingLearnerSelection} getLearnerData={getLearnerData} getLearnerModuleMastery={getLearnerModuleMastery} viewingLearnerId={viewingLearnerId} nav={nav} practice={practice} assessment={assessment} handleStartAssessment={handleStartAssessment} handleAssessmentComplete={handleAssessmentComplete} handleResumeAssessment={handleResumeAssessment} contentReady={contentReady} data-id="element-756" />
      </AuthGuard>
    </MotionConfig>;
}